import type { ComponentType } from "react";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { useFormStore } from "@/widgets/classic-form/form";
import { GroupRemoveItem } from "@/features/fields-grid";
import type { FieldMapperProps } from "@/features/fields-grid";
import type { EventFieldset } from "@/entities/form-metadata";
import { EventAssertion, FieldType } from "@/entities/form-metadata";
import { Grid } from "@/shared/components/Grid";
import { Show } from "@/shared/components/Show";
import type { YearRangeGridConfig } from "../gridLayout";
import styles from "./EventInstanceRow.module.scss";

interface EventInstanceRowProps {
    instanceIndex: number;
    fieldset: EventFieldset;
    fieldPath: string;
    onRemove: () => void;
    FieldMapper: ComponentType<FieldMapperProps<EventAssertion>>;
    gridConfig: YearRangeGridConfig;
}

export const EventInstanceRow: React.FC<EventInstanceRowProps> = ({
    fieldset,
    instanceIndex,
    fieldPath,
    onRemove,
    FieldMapper,
    gridConfig,
}) => {
    const { t } = useTranslation();
    const isRangeMode = useFormStore(({ values }) => Boolean(get(values, `${fieldPath}.isRangeMode`)));

    const getRemovable = () => (
        <span className={styles.removable}>
            <GroupRemoveItem
                ariaLabel={t("removeInstance", {
                    instanceName: fieldset.label || "",
                    instanceIndex: instanceIndex + 1,
                })}
                testId={`removeInstance-${fieldPath}`}
                removeItem={onRemove}
            />
        </span>
    );

    return (
        <fieldset>
            <Grid.Container>
                <Grid.Field spanDesktop={2} spanTablet={12} spanMobile={12}>
                    {/* Desktop Breakpoint */}
                    <Show.Above breakpoint={700}>
                        <legend className={styles.label}>{fieldset.label}</legend>
                    </Show.Above>

                    {/* Mobile Breakpoint */}
                    <Show.Below breakpoint={700}>
                        <Grid.Container>
                            <Grid.Field spanMobile={11} spanDesktop={11} spanTablet={11}>
                                <div>{fieldset.label}</div>
                            </Grid.Field>
                            {gridConfig.Removable && (
                                <Grid.Field spanMobile={1} spanTablet={1} spanDesktop={1}>
                                    {getRemovable()}
                                </Grid.Field>
                            )}
                        </Grid.Container>
                    </Show.Below>
                </Grid.Field>

                <Grid.Field spanMobile={12} spanDesktop={9} spanTablet={9}>
                    <Grid.Container>
                        {fieldset.fields.map((field) => {
                            // Hide Day and Month fields only when the user is in range mode
                            const isDayOrMonth =
                                field.assertion === EventAssertion.Day || field.assertion === EventAssertion.Month;
                            if (isRangeMode && isDayOrMonth) {
                                return null;
                            }

                            const gridKey = field.assertion;
                            return (
                                <Grid.Field
                                    key={field.assertion}
                                    spanDesktop={gridConfig.Assertions.desktop[gridKey]}
                                    spanTablet={gridConfig.Assertions.tablet[gridKey]}
                                    spanMobile={gridConfig.Assertions.mobile[gridKey]}
                                >
                                    {field.fieldType !== FieldType.YearRange && (
                                        <label className="textsml">{field.label}</label>
                                    )}
                                    <FieldMapper
                                        fieldData={field}
                                        fieldPath={fieldPath}
                                        assertion={field.assertion}
                                        inputProperties={{
                                            "aria-label": `${fieldset.label ?? ""} ${field.label ?? ""}`,
                                        }}
                                    />
                                </Grid.Field>
                            );
                        })}
                    </Grid.Container>
                </Grid.Field>

                {gridConfig.Removable && (
                    <Show.Above breakpoint={700}>
                        <Grid.Field spanMobile={2} spanTablet={2} spanDesktop={2}>
                            {getRemovable()}
                        </Grid.Field>
                    </Show.Above>
                )}
            </Grid.Container>
        </fieldset>
    );
};
